
import React from "react";
import { ArrowRight, Shield, Eye, AlertTriangle, BarChart3, Bell, Users, FileCheck } from "lucide-react";
import { Link } from "react-router-dom";

interface QuizStartProps {
  onStart: () => void;
}

const QuizStart: React.FC<QuizStartProps> = ({ onStart }) => {
  const coverageItems = [
    { icon: Shield, text: "Oversight structure and responsibilities" },
    { icon: Eye, text: "Visibility into AI use (inventory/materiality)" },
    { icon: AlertTriangle, text: "Risk ownership, thresholds, and escalation" },
    { icon: BarChart3, text: "Reporting cadence and board-level metrics" },
    { icon: Bell, text: "Incident readiness and communications expectations" },
    { icon: Users, text: "Vendor/third-party AI oversight" },
    { icon: FileCheck, text: "Assurance and documentation readiness" },
  ];

  return (
    <section className="section">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="gradient-bg text-white p-8 md:p-12 text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Board AI Oversight Assessment</h1>
              <p className="text-xl md:text-2xl font-medium mb-4">
                Benchmark your board's readiness in 5 minutes.
              </p>
              <p className="text-lg max-w-2xl mx-auto opacity-90">
                A short diagnostic designed for directors and board-facing leaders. Identify the most important oversight gaps—and what to do next.
              </p>
            </div>
            
            <div className="p-8">
              {/* What it covers */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-6">What it covers</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {coverageItems.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What you'll get */}
              <div className="border-t border-border pt-8 mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-6">What you'll get</h2>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary text-sm font-medium">✓</span>
                    </div>
                    <span className="text-muted-foreground">AI Oversight Maturity Score</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary text-sm font-medium">✓</span>
                    </div>
                    <span className="text-muted-foreground">Strengths and gaps</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary text-sm font-medium">✓</span>
                    </div>
                    <span className="text-muted-foreground">Prioritized 30/60/90 roadmap</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary text-sm font-medium">✓</span>
                    </div>
                    <div className="text-muted-foreground">
                      <span className="block mb-2">Recommended next steps:</span>
                      <ul className="ml-4 space-y-1 text-sm">
                        <li>
                          <span className="text-muted-foreground/80">Foundational capability → </span>
                          <Link to="/blueprint" className="text-primary hover:underline">Director AI Oversight Intensive</Link>
                        </li>
                        <li>
                          <span className="text-muted-foreground/80">Tailored operationalization → </span>
                          <Link to="/advisory" className="text-primary hover:underline">Advisory</Link>
                        </li>
                        <li>
                          <span className="text-muted-foreground/80">Ongoing support → </span>
                          <Link to="/continuous-governance" className="text-primary hover:underline">Subscription</Link>
                        </li>
                      </ul>
                    </div>
                  </li>
                </ul>
              </div>
              
              {/* CTA */}
              <div className="text-center">
                <button 
                  onClick={onStart}
                  className="btn-primary text-lg px-8 py-3 inline-flex items-center gap-2"
                >
                  Begin Assessment
                  <ArrowRight className="w-5 h-5" />
                </button>
                <p className="text-sm text-muted-foreground mt-4">
                  Your data is secure and will only be used to generate your personalized results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuizStart;
